import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../lib/issue-reporter';
import { ReportProblemDialog } from './ReportProblem';

interface ErrorBoundaryState {
  ref?: string;
  failed: boolean;
  reportOpen: boolean;
}

/**
 * Last line of defence: if a render error escapes, show a calm message with
 * the error reference instead of a blank frame, and offer the report dialog
 * already linked to that reference.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false, reportOpen: false };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const ref = reportError(error, {
      boundary: 'app',
      componentStack: (info.componentStack || '').trim().split('\n').slice(0, 3).join(' | '),
    });
    this.setState({ ref });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="crash-panel" role="alert">
        <h1 className="crash-panel__title">Something went wrong on our side</h1>
        <p>
          We have been told about it automatically
          {this.state.ref ? (
            <>
              {' '}(reference <strong>{this.state.ref}</strong>)
            </>
          ) : null}
          . Reloading the page usually fixes it.
        </p>
        <div className="crash-panel__actions">
          <button type="button" className="report-problem__primary" onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <button
            type="button"
            className="report-problem__secondary"
            onClick={() => this.setState({ reportOpen: true })}
          >
            Tell us what happened
          </button>
        </div>
        <ReportProblemDialog
          open={this.state.reportOpen}
          onClose={() => this.setState({ reportOpen: false })}
          relatedRef={this.state.ref}
        />
      </div>
    );
  }
}
